import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { IntroScreen } from './IntroScreen';
import { SimulatorNav } from './SimulatorNav';
import { StepFrame } from './StepFrame';
import { PatientCaseStep } from './PatientCaseStep';
import { DrugStep } from './DrugStep';
import { DoseStep } from './DoseStep';
import { ResponseStep } from './ResponseStep';
import { ActionButton } from './ActionButton';
import { ClinicalReadout, ReadoutField } from './ClinicalReadout';
import { InsightLogPanel } from './InsightLogPanel';
import { useSimulator } from '../hooks/useSimulator';
import { productById } from '../data/products';
import { formatDose } from '../utils/treatmentModel';
import type { Step } from '../types/simulation';
type SimulatorProps = {
  showInsightLog?: boolean;
  /** Compact, intrinsic-height layout for fit-to-screen previews. */
  fit?: boolean;
};
const EASE = [0.23, 1, 0.32, 1] as const;
const ACTIVE_FIELD: Record<Step, ReadoutField> = {
  1: 'patient',
  2: 'product',
  3: 'dose',
  4: 'response'
};
export function Simulator({
  showInsightLog = false,
  fit = false
}: SimulatorProps) {
  const reduced = useReducedMotion() ?? false;
  const sim = useSimulator();
  const {
    patient,
    step,
    treatment,
    productId
  } = sim;
  const fade = {
    initial: {
      opacity: 0,
      x: reduced ? 0 : 8
    },
    animate: {
      opacity: 1,
      x: 0
    },
    exit: {
      opacity: 0,
      x: reduced ? 0 : -8
    },
    transition: {
      duration: 0.2,
      ease: EASE
    }
  };
  return <div className={`w-full bg-paper font-sans text-ink ${fit ? '' : 'min-h-screen'}`} data-id="element-307" data-mp="23kgaf9cpqbi4">
      <div className={`mx-auto max-w-6xl px-4 sm:px-5 md:px-8 ${fit ? 'py-6' : 'py-6 lg:py-12'}`} data-id="element-308" data-mp="28jsw3qrulcj4">
        <header className={`flex flex-wrap items-end justify-between gap-4 ${fit ? 'mb-4' : 'mb-6 md:mb-8'}`} data-id="element-309" data-mp="2diw2bejkdoso">
          <div data-id="element-310" data-mp="3jz82tm9uzirf">
            <p className="text-sm font-medium text-accent" data-id="element-311" data-mp="3jzra891vuor7">REKOVELLE · Individualized ovarian stimulation</p>
            {sim.screen === 'case' && !fit && <p className="mt-2 font-serif text-2xl font-normal tracking-[-0.01em] sm:text-3xl md:text-[44px] md:leading-tight" data-id="element-312" data-mp="3ozvvsdhne9oa">
                Can you find the right ovarian response?
              </p>}
          </div>
          {sim.screen === 'case' && !fit && <p className="hidden max-w-xs text-[15px] text-graphite md:block md:text-right" data-id="element-313" data-mp="397bk7jo523mw">
              Show us what you do today. Then compare it with an individualized approach.
            </p>}
        </header>

        <main data-id="element-314" data-mp="2bkcnj8xg4vp0">
          <AnimatePresence mode="wait" initial={false} data-id="element-315" data-mp="2bkurkrqcoe7h">
            {sim.screen === 'intro' ? <motion.div key="intro" {...fade} data-id="element-316" data-mp="2q9qw4et7793c">
                <IntroScreen onStart={sim.startFromIntro} data-id="element-317" data-mp="2qajtwqa6io9c" />
              </motion.div> : <motion.div key="case" {...fade} data-id="element-318" data-mp="2lh7jpl68yk04">
                <SimulatorNav patients={sim.patients} currentIndex={sim.patientIndex} onBack={sim.goBack} onIntro={sim.showIntro} onSelectPatient={sim.selectPatient} onReplay={sim.replayCase} onSkip={sim.skipPatient} data-id="element-319" data-mp="1xsegr4r7jbw0" />
                <StepFrame patientId={patient.id} step={sim.displayStep} canNavigate={sim.canNavigate} onNavigate={sim.navigateTo} footer={<ClinicalReadout patient={patient} product={productId ? productById(productId).name : null} dose={treatment && step === 4 ? formatDose(treatment.dose, treatment.unit) : null} response={sim.zone ? {
              label: sim.zone.short,
              className: sim.zone.textClass
            } : null} active={ACTIVE_FIELD[step]} data-id="element-321" data-mp="r1sd7pf66sg1" />} data-id="element-320" data-mp="d4lshpavyjo6">
                  <AnimatePresence mode="wait" initial={false} data-id="element-322" data-mp="36tg71byw04um">
                    <motion.div key={`${patient.id}-${step}`} {...fade} data-id="element-323" data-mp="36uznb2kjmfwe">
                      {step === 1 && <PatientCaseStep patient={patient} adjusted={sim.adjusted} onAdjust={sim.adjustPatient} onResetValues={sim.resetPatientValues} actions={<ActionButton variant="go" onClick={sim.startCase} data-id="element-325" data-mp="38dhps3tvx0fz">
                              Start case
                            </ActionButton>} data-id="element-324" data-mp="3n2nolqot8onc" />}

                      {step === 2 && <DrugStep value={productId} onChange={sim.selectProduct} actions={<>
                              <ActionButton variant="text" onClick={sim.goBack} data-id="element-327" data-mp="2knnf9sx1fnyw">
                                Back
                              </ActionButton>
                              <ActionButton onClick={sim.confirmProduct} disabled={!productId} data-id="element-328" data-mp="w2zr5e4me41h">
                                Continue
                              </ActionButton>
                            </>} data-id="element-326" data-mp="3vzy4jpckp1yl" />}

                      {step === 3 && productId && <DoseStep productId={productId} patient={patient} rekovelleDose={sim.indiv} selection={sim.doseSelection} manualValue={sim.manualDose} manualError={sim.manualError} onSelect={sim.selectDose} onManualChange={sim.changeManualDose} actions={<>
                              <ActionButton variant="text" onClick={sim.goBack} data-id="element-330" data-mp="3uk3s46uj275g">
                                Back
                              </ActionButton>
                              <ActionButton onClick={sim.confirmDose} disabled={!sim.doseReady} data-id="element-331" data-mp="11q542fzn2yo2">
                                {productId === 'rekovelle' ? 'Use this dose' : 'Continue'}
                              </ActionButton>
                            </>} data-id="element-329" data-mp="19v5xy9lvuyo9" />}

                      {step === 4 && treatment && <ResponseStep patient={patient} treatment={treatment} indiv={sim.indiv} hasRun={sim.hasRun} runKey={sim.runKey} revealed={sim.revealed} reduced={reduced} onRun={sim.runResponse} onReplay={sim.replayResponse} onReveal={sim.reveal} actions={<>
                              <ActionButton variant="text" onClick={sim.changeChoice} data-id="element-333" data-mp="2i2pek733btqh">
                                Change my choice
                              </ActionButton>
                              {sim.revealed && sim.hasRun && <ActionButton onClick={sim.nextPatient} data-id="element-334" data-mp="285uhlsmgu1eb">Try another patient</ActionButton>}
                            </>} data-id="element-332" data-mp="1kmwm6m3g37qj" />}
                    </motion.div>
                  </AnimatePresence>
                </StepFrame>
              </motion.div>}
          </AnimatePresence>
        </main>

        <footer className="mt-6 flex flex-col gap-1 text-xs leading-relaxed text-graphite/80 md:flex-row md:justify-between md:gap-8" data-id="element-335" data-mp="1ubttdst53ws7">
          <p data-id="element-336" data-mp="1gdlpf0xorv7j">Interactive model for educational demonstration. Clinical decisions must follow the approved REKOVELLE prescribing information.</p>
          <p className="md:max-w-sm md:text-right" data-id="element-337" data-mp="1kfqfjpoxe9pf">
            REKOVELLE dose follows the labelled AMH (Elecsys® AMH Plus) + body-weight algorithm, first cycle. Response positions are illustrative and do not represent dose equivalence between products.
          </p>
        </footer>

        {showInsightLog && <InsightLogPanel events={sim.events} onClear={sim.clearEvents} data-id="element-338" data-mp="1lw5xgmfvvtbo" />}
      </div>
    </div>;
}